import React, { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";

const photos = [
  { src: "/festival.png", alt: "Festival Ca Pe Vremuri" },
  { src: "/festival 3 standuri.jpeg", alt: "Standuri Festival" },
  { src: "/festival 4 standuri.jpeg", alt: "Standuri Festival" },
  { src: "/hora festival.jpeg", alt: "Hora la Festival" },
  { src: "/standuri.jpeg", alt: "Standuri" },
  { src: "/stand manual.jpeg", alt: "Stand Manual" },
  { src: "/bautura stand.jpeg", alt: "Stand Băuturi" },
  { src: "/oua stand.jpeg", alt: "Stand Ouă" },
];

const PhotoGallery: React.FC = () => {
  const { language } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [maxIndex, setMaxIndex] = useState(photos.length - 1);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  // Calculate max index based on how many photos fit in viewport
  useEffect(() => {
    const calculateMaxIndex = () => {
      if (scrollRef.current) {
        const containerWidth = scrollRef.current.offsetWidth;
        const itemWidth = containerWidth * 0.9 + 16; // width + gap
        const visibleItems = Math.floor(containerWidth / itemWidth) || 1;
        const calculatedMax = Math.max(0, photos.length - visibleItems);
        setMaxIndex(calculatedMax);
      }
    };

    calculateMaxIndex();
    window.addEventListener("resize", calculateMaxIndex);
    return () => window.removeEventListener("resize", calculateMaxIndex);
  }, []);

  const scrollLeft = () => {
    if (scrollRef.current && currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      const itemWidth = scrollRef.current.offsetWidth * 0.9 + 16;
      scrollRef.current.scrollTo({
        left: newIndex * itemWidth,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current && currentIndex < maxIndex) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      const itemWidth = scrollRef.current.offsetWidth * 0.9 + 16;
      scrollRef.current.scrollTo({
        left: newIndex * itemWidth,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const scrollPosition = scrollRef.current.scrollLeft;
      const itemWidth = scrollRef.current.offsetWidth * 0.9 + 16;
      const newIndex = Math.min(
        Math.round(scrollPosition / itemWidth),
        maxIndex,
      );
      setCurrentIndex(newIndex);
    }
  };

  const openImage = (src: string) => {
    setSelectedImage(src);
  };

  const closeImage = () => {
    setSelectedImage(null);
  };

  return (
    <section className="section bg-gray-50">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-romanian-red font-semibold text-sm uppercase tracking-wider">
            {language === "ro" ? "Galerie Foto" : "Galleria Fotografica"}
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-romanian-blue mt-2 mb-4">
            {language === "ro"
              ? "Momente de la Festival"
              : "Momenti dal Festival"}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-romanian-blue via-romanian-yellow to-romanian-red mx-auto" />
        </div>

        {/* Gallery */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={scrollLeft}
            disabled={currentIndex === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all disabled:opacity-30 disabled:cursor-not-allowed -ml-4 md:ml-0"
          >
            <ChevronLeft className="w-6 h-6 text-romanian-blue" />
          </button>

          <button
            onClick={scrollRight}
            disabled={currentIndex >= maxIndex}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-white transition-all disabled:opacity-30 disabled:cursor-not-allowed -mr-4 md:mr-0"
          >
            <ChevronRight className="w-6 h-6 text-romanian-blue" />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 px-8 md:px-12"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {photos.map((photo, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[90%] md:w-[65%] lg:w-[50%] snap-center cursor-pointer"
                onClick={() => openImage(photo.src)}
              >
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl group">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            ))}
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-6 gap-2">
            {photos.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                  if (scrollRef.current) {
                    const itemWidth = scrollRef.current.offsetWidth * 0.9 + 16;
                    scrollRef.current.scrollTo({
                      left: index * itemWidth,
                      behavior: "smooth",
                    });
                  }
                }}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  currentIndex === index
                    ? "bg-romanian-blue w-8"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={closeImage}
        >
          <button
            onClick={closeImage}
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/75 transition-colors"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={selectedImage}
              alt="Enlarged view"
              className="w-full h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default PhotoGallery;
